import React from "react";
import cn from "./cn";

/**
 * PanelButton — a bordered panel that is a button.
 *
 * The clickable surface that is too big to be an `AppButton`: an option in a
 * picker, a summary row that opens a detail screen, a tile that filters a list.
 * Full width, block content inside, and the app's two interactive cues — the
 * border moves to the brand on hover, and focus draws the ring.
 *
 * It is deliberately NOT an `AppButton` variant. That component is a CONTROL:
 * a min-height, inline padding and a size vocabulary, every one of which a
 * panel would have to override to be a panel. Two components, not one with a
 * branch.
 *
 * The panel owns its own geometry and its states; everything it CONTAINS is
 * `children`.
 *
 * ⚠️ 10px is `tw-rounded-md`. The radius scale is remapped in
 * `tailwind.config.js` and `tw-rounded-lg` would be 16px.
 *
 * ⚠️ `tw-border-solid` is not decoration. Preflight is off, so nothing in this
 * app sets a global `border-style` — a bare `tw-border` is a width on a border
 * whose style is still the initial `none`, and it paints nothing. On a
 * `<button>` it is worse than nothing: the UA default is `outset`.
 */
const PANEL_BASE_CLASSES =
  "tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3.5 tw-text-left " +
  "tw-rounded-md tw-border tw-border-solid tw-px-3.5 tw-py-[11px] " +
  "tw-transition-[border-color,background-color] tw-duration-150 " +
  // Without these a disabled panel looks identical to an enabled one, which is
  // the silently-wrong shape this library exists to remove.
  "disabled:tw-cursor-not-allowed disabled:tw-opacity-60 " +
  "tw-focus-ring";

// The border and fill for each state, resolved to exactly one set: `cn()` does
// not merge, so a selected panel must not also carry the idle border.
const PANEL_TONE_CLASSES = {
  idle:
    "tw-border-line tw-bg-surface-raised hover:tw-border-primary/50 hover:tw-bg-surface-hover " +
    "disabled:hover:tw-border-line disabled:hover:tw-bg-surface-raised",
  selected: "tw-border-primary tw-bg-primary-soft",
};

/**
 * Props:
 *   selected — for a panel that is one choice of several (a picker option).
 *              Draws the chosen state and sets `aria-pressed`, so the choice is
 *              announced and not carried by colour alone. Omit it for a panel
 *              that is simply an action.
 */
const PanelButton = React.forwardRef(({ className = "", selected, children, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-pressed={selected === undefined ? undefined : !!selected}
    className={cn(PANEL_BASE_CLASSES, PANEL_TONE_CLASSES[selected ? "selected" : "idle"], className)}
    {...props}
  >
    {children}
  </button>
));
PanelButton.displayName = "PanelButton";

export default PanelButton;
